import React from "react";
import Container from "react-bootstrap/Container";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import { StepNavHeader } from "../components/assignments/StepNavHeader";
import {
  StudentSelector,
  StudentSelectorMode,
} from "../components/assignments/StudentSelector";
import { useTranslation } from "react-i18next";
import { LessonsSelector } from "../components/assignments/LessonsSelector";
import { useLiveData, packLessonIdAndCustomFlashpackId } from "../common/utils";
import { Account, AccountContext } from "../common/context/AccountContext";
import { SchoolStudent } from "../common/api";
import { Lesson } from "../common/api";
import { Confirmation } from "../components/assignments/Confirmation";
import styled from "styled-components";

const Page = styled.div`
  margin-top: 12px;
`;

export const LessonAssignmentsPage: React.FC<{
  initialLesson?: Lesson;
  initialUnitId?: number;
  onCancel?: () => void;
}> = ({ initialLesson, initialUnitId, onCancel }) => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [selectedStudents, setSelectedStudents] = React.useState<
    ReadonlyMap<number, Readonly<SchoolStudent>>
  >(new Map());
  const [selectedLessons, setSelectedLessons] = React.useState<
    ReadonlyMap<string, Lesson> // "[lesson_id]_[custom_flashpack_id?]"
  >(() => {
    if (!initialLesson) {
      return new Map();
    }
    return new Map([
      [
        packLessonIdAndCustomFlashpackId(
          initialLesson.id,
          initialLesson.custom_flashpack_id
        ),
        initialLesson,
      ],
    ]);
  });
  const [selectedLessonsSortOrder, setSelectedLessonsSortOrder] =
    React.useState<Array<string>>(() =>
      initialLesson
        ? [
            packLessonIdAndCustomFlashpackId(
              initialLesson.id,
              initialLesson.custom_flashpack_id
            ),
          ]
        : []
    ); // "[lesson_id]_[custom_flashpack_id?]"
  const isNextEnabled =
    (activeStep === 0 && selectedStudents.size > 0) ||
    (activeStep === 1 && selectedLessons.size > 0);
  const { t } = useTranslation();
  const steps = [
    t("assignments_step_pick_students"),
    t("assignments_step_pick_lessons"),
    t("assignments_step_confirm"),
  ];
  const { accountLiveData } = React.useContext(AccountContext);
  const [account, setAccount] = React.useState<Account>();

  useLiveData(
    React.useCallback((account: Account) => {
      if (account.hasMaybeProfileState()) {
        setAccount(account);
      }
    }, []),
    accountLiveData
  );

  if (!account) {
    return <></>;
  }

  return (
    <Page>
      <Container style={{ maxWidth: "800px" }}>
        <StepNavHeader
          isReplaceBackWithCancel={activeStep === 0}
          onBack={() => setActiveStep(activeStep - 1)}
          onCancel={onCancel}
          header={steps[activeStep]}
          isNextVisible={activeStep < steps.length - 1}
          isNextEnabled={isNextEnabled}
          onNext={() => setActiveStep(activeStep + 1)}
        />
        <Stepper activeStep={activeStep}>
          {steps.map((label, index) => {
            const stepProps: { completed?: boolean } = {};
            return (
              <Step key={label} {...stepProps}>
                <StepLabel>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Container>
      {activeStep === 0 && (
        <StudentSelector
          account={account}
          mode={StudentSelectorMode.LESSON_ASSIGNMENT}
          selectedStudents={selectedStudents}
          setSelectedStudents={setSelectedStudents}
          alreadyAssignedStudentIds={[]}
        />
      )}
      {activeStep === 1 && (
        <LessonsSelector
          account={account}
          selectedLessons={selectedLessons}
          setSelectedLessons={setSelectedLessons}
          initialUnitId={initialUnitId}
        />
      )}
      {activeStep === 2 && (
        <Confirmation
          account={account}
          selectedStudents={selectedStudents}
          selectedLessons={selectedLessons}
          setSelectedLessons={setSelectedLessons}
          selectedLessonsSortOrder={selectedLessonsSortOrder}
          setSelectedLessonsSortOrder={setSelectedLessonsSortOrder}
        />
      )}
    </Page>
  );
};
